import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Switch,
  FormControlLabel,
  Rating,
  Badge,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Store as StoreIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  QrCode as QrCodeIcon,
  PhotoCamera as PhotoCameraIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Inventory as InventoryIcon,
  Assignment as OrderIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Favorite as FavoriteIcon,
  AddBusiness as AddBusinessIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  LocalShipping as ShippingIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    margin: theme.spacing(0, 1),
    marginBottom: theme.spacing(2)
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: '1px solid #e0e0e0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: theme.spacing(1.5),
    '&:hover': {
      transform: 'none' // Disable hover transform on mobile
    }
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.spacing(1),
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
  }
}));

const MetricCard = styled(Card)(({ theme, gradient }) => ({
  background: gradient,
  color: 'white',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '100%',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '0 0 0 100%',
  }
}));

const MyStore = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [storeStatus, setStoreStatus] = useState(true);
  
  // Ensure storeStatus is always a boolean to prevent MUI capitalize errors
  const safeStoreStatus = Boolean(storeStatus);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const bannerRef = useRef(null);

  // Mock Store Data
  const storeData = {
    name: 'গ্রামীণ বাজার',
    description: 'তাজা ও অর্গানিক পণ্যের বিশ্বস্ত দোকান',
    address: '১২ মেইন রোড, ধানমন্ডি, ঢাকা',
    phone: '০১৭১২৩৪৫৬৭২',
    email: 'info@grameenbazar.com',
    location: {
      district: 'ঢাকা',
      upazilla: 'ধানমন্ডি',
      bazar: 'নিউ মার্কেট'
    },
    rating: 4.8,
    totalReviews: 342,
    isVerified: true,
    isOpen: true,
    openTime: '08:00 AM',
    closeTime: '10:00 PM',
    deliveryRadius: '১০ কিমি',
    joinDate: '১৫ জানুয়ারি ২০২৩'
  };

  const storeMetrics = {
    totalProducts: 156,
    totalOrders: 1847,
    totalCustomers: 892,
    monthlyRevenue: 185400,
    todayVisitors: 234,
    monthlyVisitors: 8450,
    conversionRate: 3.2,
    avgOrderValue: 285,
    repeatCustomers: 68,
    returnRate: 2.1
  };

  const recentActivities = [
    { type: 'order', message: 'নতুন অর্ডার পেয়েছেন - ৳৮৫০', time: '১০ মিনিট আগে', icon: <ShoppingCartIcon /> },
    { type: 'review', message: 'রহিম উদ্দিন ৫ স্টার রিভিউ দিয়েছেন', time: '৩০ মিনিট আগে', icon: <StarIcon /> },
    { type: 'customer', message: 'নতুন গ্রাহক যোগ হয়েছেন', time: '১ ঘন্টা আগে', icon: <PeopleIcon /> },
    { type: 'product', message: 'টমেটো স্টক কমে গেছে', time: '২ ঘন্টা আগে', icon: <InventoryIcon /> },
    { type: 'payment', message: 'পেমেন্ট রিসিভ হয়েছে - ৳১২৫০', time: '৩ ঘন্টা আগে', icon: <MoneyIcon /> }
  ];

  const topProducts = [
    { name: 'টাজা টমেটো', sales: 245, revenue: 29400, rating: 4.8 },
    { name: 'দেশি পেঁয়াজ', sales: 189, revenue: 15120, rating: 4.6 },
    { name: 'বাসমতি চাল', sales: 156, revenue: 28080, rating: 4.9 },
    { name: 'মুরগির মাংস', sales: 98, revenue: 34300, rating: 4.7 },
    { name: 'গরুর দুধ', sales: 234, revenue: 15210, rating: 4.5 }
  ];

  const recentReviews = [
    { customer: 'করিম মিয়া', rating: 5, comment: 'খুবই ভালো পণ্য। তাজা এবং দামও সাশ্রয়ী।', time: '২ দিন আগে' },
    { customer: 'ফাতেমা খাতুন', rating: 4, comment: 'ডেলিভারি সময়মতো হয়েছে। পণ্যের মান ভালো।', time: '৩ দিন আগে' },
    { customer: 'রহিম উদ্দিন', rating: 5, comment: 'চমৎকার সার্ভিস। আবার কিনব।', time: '৫ দিন আগে' }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleStoreStatusToggle = () => {
    setStoreStatus(!storeStatus);
  };

  const handleQrDialogOpen = () => {
    setQrDialogOpen(true);
  };

  // Generate QR code when dialog opens
  useEffect(() => {
    if (qrDialogOpen) {
      const generateQR = async () => {
        const storeUrl = `https://grameenbazar.com/store/${storeData.name}`;
        const qrDataUrl = await generateQRCode(storeUrl, { width: 200 });
        setQrCodeDataUrl(qrDataUrl);
      };
      generateQR();
    }
  }, [qrDialogOpen, storeData.name]);

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
    setQrCodeDataUrl(''); // Reset QR code when closing
  };

  const generateQRCode = async (text, options = {}) => {
    try {
      const qrOptions = {
        width: options.width || 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M',
        ...options
      };
      
      const dataUrl = await QRCode.toDataURL(text, qrOptions);
      return dataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  const handleDownloadBanner = async () => {
    try {
      if (!bannerRef.current) return;
      
      // Generate high-quality QR code for banner
      const storeUrl = `https://grameenbazar.com/store/${storeData.name}`;
      const qrDataUrl = await generateQRCode(storeUrl, { width: 300 });
      
      // Create banner element
      const bannerElement = bannerRef.current;
      
      // Update QR code in banner
      const qrImg = bannerElement.querySelector('#banner-qr-code');
      if (qrImg && qrDataUrl) {
        qrImg.src = qrDataUrl;
      }
      
      // Wait for image to load
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Convert banner to canvas
      const canvas = await html2canvas(bannerElement, {
        scale: 3, // High resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Create PDF (6 inch height x 3 inch width at 300 DPI)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [3, 6] // width, height in inches
      });
      
      // Add banner image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 3, 6);
      
      // Download PDF
      pdf.save(`${storeData.name}-store-banner.pdf`);
      
    } catch (error) {
      console.error('Error generating banner:', error);
    }
  };

  const handleCopyStoreLink = () => {
    const storeUrl = `https://grameenbazar.com/store/${storeData.name}`;
    navigator.clipboard.writeText(storeUrl).then(() => {
      // You can add a snackbar notification here
      console.log('Store link copied to clipboard');
    });
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  const getActivityIcon = (type) => {
    const iconProps = { sx: { fontSize: 20 } };
    switch (type) {
      case 'order': return <ShoppingCartIcon {...iconProps} />;
      case 'review': return <StarIcon {...iconProps} />;
      case 'customer': return <PeopleIcon {...iconProps} />;
      case 'product': return <InventoryIcon {...iconProps} />;
      case 'payment': return <MoneyIcon {...iconProps} />;
      default: return <CheckCircleIcon {...iconProps} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'order': return '#1976d2';
      case 'review': return '#ffa726';
      case 'customer': return '#66bb6a';
      case 'product': return '#ef5350';
      case 'payment': return '#26a69a';
      default: return '#757575';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      py: 3,
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Store Header */}
      <StyledPaper>
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
          <Grid item xs={12} sm={4} md={2}>
            <Box sx={{ position: 'relative', textAlign: 'center' }}>
              <Avatar
                sx={{ 
                  width: { xs: 80, sm: 100, md: 120 }, 
                  height: { xs: 80, sm: 100, md: 120 }, 
                  mx: 'auto',
                  fontSize: { xs: 32, sm: 40, md: 48 },
                  bgcolor: 'primary.main',
                  border: { xs: '2px solid #e3f2fd', md: '4px solid #e3f2fd' }
                }}
              >
                <StoreIcon sx={{ fontSize: { xs: 40, sm: 50, md: 60 } }} />
              </Avatar>
              <IconButton
                sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: '50%', 
                  transform: 'translateX(50%)',
                  bgcolor: 'white',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                size="small"
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} sm={8} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                {storeData.name}
              </Typography>
              {storeData.isVerified && (
                <Tooltip title="ভেরিফাইড দোকান">
                  <VerifiedIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                </Tooltip>
              )}
              <Chip 
                label={safeStoreStatus ? 'খোলা' : 'বন্ধ'} 
                color={safeStoreStatus ? 'success' : 'error'}
                size="small"
              />
            </Box>
            
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {storeData.description}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating value={storeData.rating} precision={0.1} readOnly size="small" />
                <Typography variant="body2">
                  {storeData.rating} ({storeData.totalReviews} রিভিউ)
                </Typography>
              </Box>
              
              <Chip 
                icon={<LocationIcon />} 
                label={`${storeData.location.upazilla}, ${storeData.location.district}`}
                variant="outlined"
                size="small"
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">{storeData.phone}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {storeData.openTime} - {storeData.closeTime}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row', md: 'row' }
              }}>
                <Button 
                  variant="contained" 
                  startIcon={<EditIcon />}
                  fullWidth
                  size="medium"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.875rem' } }}
                >
                  দোকান সম্পাদনা
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<ShareIcon />}
                  fullWidth={{ xs: true, sm: false, md: false }}
                  size="medium"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.875rem' } }}
                >
                  শেয়ার
                </Button>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row', md: 'row' }
              }}>
                <Button 
                  variant="outlined" 
                  startIcon={<QrCodeIcon />}
                  fullWidth
                  onClick={handleQrDialogOpen}
                  size="medium"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.875rem' } }}
                >
                  QR কোড
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<AnalyticsIcon />}
                  fullWidth={{ xs: true, sm: false, md: false }}
                  size="medium"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.875rem' } }}
                >
                  রিপোর্ট
                </Button>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch 
                                    checked={safeStoreStatus}
                onChange={handleStoreStatusToggle}
                    color="primary"
                  />
                }
                label="দোকান চালু রাখুন"
              />
            </Box>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Key Metrics */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {storeMetrics.totalProducts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট পণ্য
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {storeMetrics.totalOrders}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট অর্ডার
                  </Typography>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {storeMetrics.totalCustomers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট গ্রাহক
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ৳{storeMetrics.monthlyRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মাসিক আয়
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <StyledPaper>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', sm: 160 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '6px 8px', sm: '12px 16px' }
            }
          }}
        >
          <Tab 
            icon={<AnalyticsIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />} 
            label="পারফরম্যান্স" 
            iconPosition="start"
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          />
          <Tab 
            icon={<StarIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />} 
            label="রিভিউ" 
            iconPosition="start"
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          />
          <Tab 
            icon={<InventoryIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />} 
            label="সেরা পণ্য" 
            iconPosition="start"
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          />
          <Tab 
            icon={<TimeIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />} 
            label="কার্যক্রম" 
            iconPosition="start"
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          />
        </Tabs>

        {/* Performance Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardHeader title="দোকানের পারফরম্যান্স" />
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">রূপান্তর হার</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {storeMetrics.conversionRate}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={storeMetrics.conversionRate * 10} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">রিপিট কাস্টমার</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {storeMetrics.repeatCustomers}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={storeMetrics.repeatCustomers} 
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">রিটার্ন রেট</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {storeMetrics.returnRate}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={storeMetrics.returnRate * 10} 
                      color="warning"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardHeader title="ভিজিটর পরিসংখ্যান" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {storeMetrics.todayVisitors}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          আজকের ভিজিটর
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {storeMetrics.monthlyVisitors}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          মাসিক ভিজিটর
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                          ৳{storeMetrics.avgOrderValue}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          গড় অর্ডার ভ্যালু
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Reviews Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={8}>
              <StyledCard>
                <CardHeader title="সাম্প্রতিক রিভিউ" />
                <CardContent>
                  <List>
                    {recentReviews.map((review, index) => (
                      <ListItem key={index} divider={index < recentReviews.length - 1}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {review.customer.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">{review.customer}</Typography>
                              <Rating value={review.rating} size="small" readOnly />
                              <Typography variant="caption" color="text.secondary">
                                {review.time}
                              </Typography>
                            </Box>
                          }
                          secondary={review.comment}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </StyledCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <StyledCard>
                <CardHeader title="রেটিং বিশ্লেষণ" />
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {storeData.rating}
                    </Typography>
                    <Rating value={storeData.rating} readOnly />
                    <Typography variant="body2" color="text.secondary">
                      {storeData.totalReviews} রিভিউর ভিত্তিতে
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2">৫ স্টার</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={75} 
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">৭৫%</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2">৪ স্টার</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={15} 
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">১৫%</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2">৩ স্টার</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={8} 
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">৮%</Typography>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Top Products Tab */}
        <TabPanel value={activeTab} index={2}>
          <StyledCard>
            <CardHeader title="সেরা বিক্রিত পণ্যসমূহ" />
            <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              <TableContainer sx={{ 
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: '#a8a8a8',
                  },
                }
              }}>
                <Table sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>র‌্যাঙ্ক</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>পণ্যের নাম</TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>বিক্রিত</TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>রেটিং</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>আয়</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                          <Avatar sx={{ 
                            bgcolor: 'primary.main', 
                            width: { xs: 24, sm: 32 }, 
                            height: { xs: 24, sm: 32 },
                            fontSize: { xs: '0.75rem', sm: '1rem' }
                          }}>
                            {index + 1}
                          </Avatar>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}>
                            {product.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: { xs: 1, sm: 2 } }}>
                          <Chip 
                            label={product.sales} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: { xs: 12, sm: 16 }, color: '#ffc107' }} />
                            <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                              {product.rating}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 600, 
                            color: 'success.main',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}>
                            ৳{product.revenue.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </TabPanel>

        {/* Recent Activities Tab */}
        <TabPanel value={activeTab} index={3}>
          <StyledCard>
            <CardHeader title="সাম্প্রতিক কার্যক্রম" />
            <CardContent>
              <List>
                {recentActivities.map((activity, index) => (
                  <ListItem key={index} divider={index < recentActivities.length - 1}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: `${getActivityColor(activity.type)}20`,
                        color: getActivityColor(activity.type)
                      }}>
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.message}
                      secondary={activity.time}
                      primaryTypographyProps={{ fontSize: 14 }}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                    {activity.type === 'order' && (
                      <Chip label="নতুন" color="primary" size="small" />
                    )}
                    {activity.type === 'product' && (
                      <Chip label="সতর্ক" color="warning" size="small" />
                    )}
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </StyledCard>
        </TabPanel>
      </StyledPaper>

      {/* QR Code Dialog */}
      <Dialog 
        open={qrDialogOpen} 
        onClose={handleQrDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={{ xs: true, sm: false }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            margin: { xs: 0, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCodeIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              দোকানের QR কোড
            </Typography>
          </Box>
          <IconButton onClick={handleQrDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          textAlign: 'center', 
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 }
        }}>
          {/* QR Code Display Area */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 3,
            p: { xs: 2, sm: 3 },
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'grey.300'
          }}>
            <Box sx={{
              width: { xs: 180, sm: 220 },
              height: { xs: 180, sm: 220 },
              bgcolor: 'white',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              p: 2
            }}>
              {/* Actual QR Code */}
              {qrCodeDataUrl ? (
                <img 
                  src={qrCodeDataUrl}
                  alt="Store QR Code"
                  style={{
                    width: window.innerWidth < 600 ? '140px' : '180px',
                    height: window.innerWidth < 600 ? '140px' : '180px',
                    borderRadius: '8px',
                    imageRendering: 'pixelated'
                  }}
                />
              ) : (
                <Box sx={{
                  width: { xs: 140, sm: 180 },
                  height: { xs: 140, sm: 180 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  borderRadius: 1
                }}>
                  <CircularProgress />
                </Box>
              )}
              <Typography variant="caption" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                {qrCodeDataUrl ? 'স্ক্যান করুন' : 'QR কোড তৈরি হচ্ছে...'}
              </Typography>
            </Box>
          </Box>

          {/* Store Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {storeData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {storeData.description}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.100',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                স্টোর লিংক:
              </Typography>
              <Typography variant="body2" color="primary.main" sx={{ 
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                wordBreak: 'break-all',
                textAlign: 'center'
              }}>
                grameenbazar.com/store/{storeData.name}
              </Typography>
            </Box>
          </Box>

          {/* Instructions */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'info.50', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'info.100'
          }}>
            <Typography variant="body2" color="info.main" sx={{ fontWeight: 500, mb: 1 }}>
              📱 কিভাবে ব্যবহার করবেন:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left' }}>
              • গ্রাহকরা QR কোড স্ক্যান করে সরাসরি আপনার দোকানে যেতে পারবেন<br/>
              • QR কোড প্রিন্ট করে দোকানে লাগিয়ে রাখুন<br/>
              • সোশ্যাল মিডিয়ায় শেয়ার করুন
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          pt: 0, 
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleCopyStoreLink}
            fullWidth
            size="large"
            sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}
          >
            লিংক কপি করুন
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadBanner}
            fullWidth
            size="large"
            sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}
          >
            QR কোড ডাউনলোড
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden Banner for PDF Generation */}
      <Box
        ref={bannerRef}
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: '900px', // 3 inches at 300 DPI
          height: '1800px', // 6 inches at 300 DPI
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          overflow: 'hidden',
          '@media (max-width: 600px)': {
            transform: 'scale(0.8)', // Slightly smaller for mobile processing
            transformOrigin: 'top left'
          }
        }}
      >
        {/* Banner Content */}
        <Box sx={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '60px 40px',
          boxSizing: 'border-box'
        }}>
          {/* Header Section */}
          <Box sx={{
            textAlign: 'center',
            color: 'white',
            width: '100%'
          }}>
            <Typography sx={{
              fontSize: '72px',
              fontWeight: 'bold',
              marginBottom: '20px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              lineHeight: 1.2
            }}>
              গ্রামীণ বাজার
            </Typography>
            <Typography sx={{
              fontSize: '32px',
              fontWeight: '500',
              marginBottom: '10px',
              opacity: 0.9
            }}>
              GRAMEEN BAZAR
            </Typography>
            <Box sx={{
              width: '200px',
              height: '4px',
              backgroundColor: 'rgba(255,255,255,0.8)',
              margin: '20px auto',
              borderRadius: '2px'
            }} />
          </Box>

          {/* Store Info Section */}
          <Box sx={{
            textAlign: 'center',
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.15)',
            padding: '40px 30px',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255,255,255,0.2)',
            width: '100%',
            maxWidth: '700px'
          }}>
            <Typography sx={{
              fontSize: '56px',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
            }}>
              {storeData.name}
            </Typography>
            <Typography sx={{
              fontSize: '28px',
              marginBottom: '20px',
              opacity: 0.9,
              fontStyle: 'italic'
            }}>
              {storeData.description}
            </Typography>
            <Typography sx={{
              fontSize: '24px',
              opacity: 0.8,
              marginBottom: '10px'
            }}>
              📍 {storeData.address}
            </Typography>
            <Typography sx={{
              fontSize: '24px',
              opacity: 0.8
            }}>
              📞 {storeData.phone}
            </Typography>
          </Box>

          {/* QR Code Section */}
          <Box sx={{
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '25px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '5px solid rgba(255,255,255,0.9)'
          }}>
            <Typography sx={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#333',
              marginBottom: '20px'
            }}>
              স্ক্যান করুন
            </Typography>
            <img
              id="banner-qr-code"
              src={qrCodeDataUrl || ''}
              alt="Store QR Code"
              style={{
                width: '300px',
                height: '300px',
                border: '3px solid #eee',
                borderRadius: '15px'
              }}
            />
            <Typography sx={{
              fontSize: '24px',
              color: '#666',
              marginTop: '15px',
              fontWeight: '500'
            }}>
              অনলাইনে অর্ডার করুন
            </Typography>
          </Box>

          {/* Footer Section */}
          <Box sx={{
            textAlign: 'center',
            color: 'white',
            width: '100%'
          }}>
            <Typography sx={{
              fontSize: '28px',
              fontWeight: '600',
              marginBottom: '10px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              www.grameenbazar.com
            </Typography>
            <Typography sx={{
              fontSize: '20px',
              opacity: 0.8
            }}>
              তাজা পণ্য • দ্রুত ডেলিভারি • বিশ্বস্ত সেবা
            </Typography>
          </Box>

          {/* Decorative Elements */}
          <Box sx={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '3px solid rgba(255,255,255,0.3)'
          }} />
          <Box sx={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '3px solid rgba(255,255,255,0.3)'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '2px solid rgba(255,255,255,0.3)'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '2px solid rgba(255,255,255,0.3)'
          }} />
        </Box>
      </Box>
    </Container>
  );
};

export default MyStore; 